import { useEffect, useMemo, useRef } from 'react';

import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { useKeyboardShortcutsContext } from '../../contexts/KeyboardShortcutsContext';
import {
  detectOS,
  eventToCombo,
  isEditableTarget,
  matchEvent,
  parseCombo,
  type HotkeyOS,
} from '../useHotkeys';
import {
  CHORD_TIMEOUT_MS,
  keyboardShortcutsRegistry,
} from '../../lib/keyboardShortcuts/keyboardShortcutsRegistry';
import type { KeyboardShortcutId, ShortcutBinding } from '../../lib/keyboardShortcuts/types';
import { useAppStore } from '../../store/store';

function normalizeBindingPart(part: string): string {
  return part
    .split('+')
    .map((segment) => segment.trim().toLowerCase())
    .filter(Boolean)
    .join('+');
}

function bindingParts(binding: ShortcutBinding): string[] {
  return Array.isArray(binding)
    ? binding.map(normalizeBindingPart)
    : [normalizeBindingPart(binding)];
}

function comboMatchesPart(part: string, event: KeyboardEvent, os: HotkeyOS): boolean {
  return matchEvent(event, parseCombo(part, os));
}

/** Last segment of a combo string (`ctrl+u` → `u`). Chord steps after the prefix ignore held modifiers. */
function comboKeyOnly(combo: string): string {
  const segments = combo.split('+').filter(Boolean);
  return segments[segments.length - 1] ?? combo;
}

function chordBindingMatchesSteps(bindingPartsList: string[], stepCombos: string[]): boolean {
  if (bindingPartsList.length !== stepCombos.length) return false;
  return bindingPartsList.every((part, index) => {
    const stepCombo = stepCombos[index];
    if (!stepCombo) return false;
    if (index === 0) return part === stepCombo;
    return comboKeyOnly(part) === comboKeyOnly(stepCombo);
  });
}

/**
 * GDrive "create" chords: ctrl+c on Mac, alt+c on Windows/Linux.
 * Must be OS-aware: treating ctrl+c as a chord on Windows blurs inputs and steals browser copy.
 */
function isCreateChordPrefix(combo: string, os: HotkeyOS): boolean {
  if (os === 'mac') return combo === 'ctrl+c';
  return combo === 'alt+c';
}

function blurActiveElementIfEditable(): void {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLElement && isEditableTarget(activeElement)) {
    activeElement.blur();
  }
}

const FILE_ACTION_BY_SHORTCUT: Partial<Record<KeyboardShortcutId, FileMenuActionEnum>> = {
  'file.aiSummary': FileMenuActionEnum.ArtificialIntelligenceSummary,
  'file.copyLink': FileMenuActionEnum.CopyLink,
  'file.createShortcut': FileMenuActionEnum.CreateShortcut,
  'file.delete': FileMenuActionEnum.Delete,
  'file.details': FileMenuActionEnum.Details,
  'file.download': FileMenuActionEnum.Download,
  'file.favorite': FileMenuActionEnum.AddToFavorites,
  'file.move': FileMenuActionEnum.Move,
  'file.open': FileMenuActionEnum.Open,
  'file.publicLink': FileMenuActionEnum.PublicLink,
  'file.rename': FileMenuActionEnum.Rename,
  'file.setPassword': FileMenuActionEnum.SetFilePassword,
  'file.share': FileMenuActionEnum.Share,
  'file.tags': FileMenuActionEnum.Tags,
};

/**
 * Global keyboard shortcut engine: registry combos + chord sequences (1.5s timeout).
 */
export function useKeyboardShortcutEngine() {
  const {
    getFilesNavigation,
    invokeFileMenuAction,
    invokeFocusSearch,
    invokeHandler,
    invokeOpenShortcutsModal,
    isEnabled,
  } = useKeyboardShortcutsContext();

  const triggerCreateFolder = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFolder,
  );
  const triggerCreateFileFromTemplate = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateFileFromTemplate,
  );
  const triggerCreateSecretFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateSecretFile,
  );
  const triggerCreateNoteFile = useAppStore(
    (state) => state.appEvents.fileCreation.triggerCreateNoteFile,
  );
  const openCreateMenu = useAppStore((state) => state.appEvents.keyboardShortcuts.openCreateMenu);

  const os = useMemo(detectOS, []);
  const chordBufferRef = useRef<{ parts: string[]; startedAt: number } | null>(null);

  const dispatchShortcut = (id: KeyboardShortcutId) => {
    switch (id) {
      case 'app.focusSearch':
        invokeFocusSearch();
        return;
      case 'app.openShortcutsHelp':
        invokeOpenShortcutsModal();
        return;
      case 'create.openMenu':
        openCreateMenu();
        return;
      case 'create.folder':
        triggerCreateFolder();
        return;
      case 'create.uploadFile':
        invokeHandler('create.uploadFile');
        return;
      case 'create.uploadFolder':
        invokeHandler('create.uploadFolder');
        return;
      case 'create.word':
        triggerCreateFileFromTemplate('word');
        return;
      case 'create.excel':
        triggerCreateFileFromTemplate('excel');
        return;
      case 'create.powerpoint':
        triggerCreateFileFromTemplate('powerpoint');
        return;
      case 'create.note':
        triggerCreateNoteFile();
        return;
      case 'create.secret':
        triggerCreateSecretFile('credentials');
        return;
      case 'create.secretCard':
        triggerCreateSecretFile('card');
        return;
      case 'create.secretIdentity':
        triggerCreateSecretFile('identity');
        return;
      case 'create.secretSshKey':
        triggerCreateSecretFile('ssh_key');
        return;
      case 'create.secretCustom':
        triggerCreateSecretFile('custom');
        return;
      case 'file.openNewTab':
        invokeHandler('file.openNewTab');
        return;
      case 'nav.toggleGridList':
        getFilesNavigation()?.onToggleGridList();
        return;
      case 'nav.focusSidebar':
        getFilesNavigation()?.onFocusLandmark('sidebar');
        return;
      case 'nav.focusMain':
        getFilesNavigation()?.onFocusLandmark('main');
        return;
      case 'nav.focusDetails':
        getFilesNavigation()?.onFocusLandmark('details');
        return;
      case 'nav.focusUploadDrawer':
        getFilesNavigation()?.onFocusLandmark('upload');
        return;
      case 'selection.selectAll':
        getFilesNavigation()?.onSelectAll();
        return;
      case 'selection.clear':
        getFilesNavigation()?.onClearSelection();
        return;
      case 'selection.toggle':
        getFilesNavigation()?.onToggleSelection();
        return;
      case 'selection.moveFocusUp':
        getFilesNavigation()?.onMoveFocus('up');
        return;
      case 'selection.moveFocusDown':
        getFilesNavigation()?.onMoveFocus('down');
        return;
      case 'selection.moveFocusLeft':
        getFilesNavigation()?.onMoveFocus('left');
        return;
      case 'selection.moveFocusRight':
        getFilesNavigation()?.onMoveFocus('right');
        return;
      case 'selection.extendUp':
        getFilesNavigation()?.onArrow('up', true);
        return;
      case 'selection.extendDown':
        getFilesNavigation()?.onArrow('down', true);
        return;
      case 'selection.extendLeft':
        getFilesNavigation()?.onArrow('left', true);
        return;
      case 'selection.extendRight':
        getFilesNavigation()?.onArrow('right', true);
        return;
      default: {
        const fileAction = FILE_ACTION_BY_SHORTCUT[id];
        if (fileAction) {
          invokeFileMenuAction(fileAction);
          return;
        }
        invokeHandler(id);
      }
    }
  };

  useEffect(() => {
    if (!isEnabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;

      const now = Date.now();
      const chordBuffer = chordBufferRef.current;
      if (chordBuffer && now - chordBuffer.startedAt > CHORD_TIMEOUT_MS) {
        chordBufferRef.current = null;
      }

      const combo = eventToCombo(event, os);
      const activeChord = chordBufferRef.current;

      // Complete an in-progress chord before editable / single-shortcut guards.
      if (activeChord && combo) {
        const nextParts = [...activeChord.parts, combo];
        for (const entry of keyboardShortcutsRegistry) {
          const binding = os === 'mac' ? entry.mac : entry.windows;
          if (!Array.isArray(binding)) continue;
          const parts = bindingParts(binding);
          if (chordBindingMatchesSteps(parts, nextParts)) {
            event.preventDefault();
            chordBufferRef.current = null;
            dispatchShortcut(entry.id);
            return;
          }
        }
        chordBufferRef.current = null;
      }

      const inEditable = isEditableTarget(event.target);
      const isSlashFocus = !inEditable && combo === '/';
      const isCreateChordStart = isCreateChordPrefix(combo, os);

      if (inEditable && !isSlashFocus && !isCreateChordStart) return;
      if (inEditable && isCreateChordStart) {
        blurActiveElementIfEditable();
      }

      if (!combo) return;

      if (isSlashFocus) {
        event.preventDefault();
        invokeFocusSearch();
        return;
      }

      for (const entry of keyboardShortcutsRegistry) {
        const binding = os === 'mac' ? entry.mac : entry.windows;
        const parts = bindingParts(binding);

        if (parts.length > 1) {
          if (parts[0] === combo && comboMatchesPart(parts[0]!, event, os)) {
            event.preventDefault();
            chordBufferRef.current = { parts: [combo], startedAt: now };
            return;
          }
          continue;
        }

        const part = parts[0];
        if (!part) continue;
        if (comboMatchesPart(part, event, os)) {
          event.preventDefault();
          dispatchShortcut(entry.id);
          return;
        }
      }

      // Plain arrows without modifiers when files navigation is active
      const filesNav = getFilesNavigation();
      if (filesNav && !event.ctrlKey && !event.metaKey && !event.altKey) {
        if (event.shiftKey && combo === 'shift+ ') {
          event.preventDefault();
          filesNav.onToggleSelection();
          return;
        }
        if (!event.shiftKey) {
          if (combo === 'arrowup') {
            event.preventDefault();
            filesNav.onArrow('up', false);
            return;
          }
          if (combo === 'arrowdown') {
            event.preventDefault();
            filesNav.onArrow('down', false);
            return;
          }
          if (combo === 'arrowleft') {
            event.preventDefault();
            filesNav.onArrow('left', false);
            return;
          }
          if (combo === 'arrowright') {
            event.preventDefault();
            filesNav.onArrow('right', false);
            return;
          }
        }
      }
    };

    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [
    getFilesNavigation,
    invokeFocusSearch,
    invokeFileMenuAction,
    invokeHandler,
    invokeOpenShortcutsModal,
    isEnabled,
    openCreateMenu,
    os,
    triggerCreateFileFromTemplate,
    triggerCreateFolder,
    triggerCreateNoteFile,
    triggerCreateSecretFile,
  ]);
}
